import { useCallback, useMemo, useReducer } from "react";

type State = {
    tokenStack: string[];
    pageIndex: number;
};

type Action = { type: "next"; nextToken: string } | { type: "previous" } | { type: "reset" };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "next": {
            const trimmed = state.tokenStack.slice(0, state.pageIndex + 1);
            trimmed.push(action.nextToken);
            return { tokenStack: trimmed, pageIndex: state.pageIndex + 1 };
        }
        case "previous":
            return { ...state, pageIndex: Math.max(0, state.pageIndex - 1) };
        case "reset":
            return { tokenStack: [""], pageIndex: 0 };
    }
}

const initialState: State = { tokenStack: [""], pageIndex: 0 };

/**
 * Manages cursor-based pagination state.
 *
 * Tracks a stack of previous page tokens so the user can navigate backwards
 * through pages without the API needing to support reverse cursors.
 */
export function useCursorPagination() {
    const [state, dispatch] = useReducer(reducer, initialState);

    const currentToken = state.tokenStack[state.pageIndex] ?? "";

    const goToNextPage = useCallback((nextToken: string) => {
        dispatch({ type: "next", nextToken });
    }, []);

    const goToPreviousPage = useCallback(() => {
        dispatch({ type: "previous" });
    }, []);

    const reset = useCallback(() => {
        dispatch({ type: "reset" });
    }, []);

    return useMemo(
        () => ({
            /** The token to pass to the current API request */
            currentToken,
            /** Zero-based index of the current page */
            pageIndex: state.pageIndex,
            /** Whether there is a previous page to navigate to */
            hasPreviousPage: state.pageIndex > 0,
            /** Navigate to the next page using the nextToken from the API response */
            goToNextPage,
            /** Navigate to the previous page */
            goToPreviousPage,
            /** Reset to the first page (e.g. when filters change) */
            reset,
        }),
        [currentToken, state.pageIndex, goToNextPage, goToPreviousPage, reset],
    );
}
